import React, {useMemo, useRef, useState} from "react";
import AppModal from "../../core/components/app-modal";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import AppButton from "@common/components/app-button";
import {ReactComponent as PrintIcon} from "@assets/icons/print.svg";
import {ReactComponent as CheckmarkCircleIcon} from "@assets/icons/checkmark-circle.svg";
import {UniversalClickHandler} from "@common/models/types/universal-click-handler.type";
import {defaultQrCodeBullets} from "../constants/default-qr-code-bullets";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {stripHtmlToText} from "../helpers/strip-html-to-text";
import {animated} from "@react-spring/web";
import QrCode from "./ui/qr-code";
import {useReactToPrint} from "react-to-print";
import {printPageStyle} from "../constants/react-to-print-page-style";
import {useModalAnimation} from "../../core/animations";
import SpinnerLoader from "@common/components/spinner-loader";

export interface ArticleQrInfoModalData {
    title: string;
    url: string;
    onPrint?: UniversalClickHandler;
}

const ArticleQrInfoModal: React.FC = () => {
    const {data, hideModal, isOpenModal} = useModalWithData<ArticleQrInfoModalData>(ModalIds.ARTICLE_QR_INFO);
    const {title, url} = data ?? {} as ArticleQrInfoModalData;
    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');
    const [printPrepare, setPrintPrepare] = useState<boolean>(false);

    const qrSrc = useMemo(() => {
        if (!url) return "";
        const encoded = encodeURIComponent(url);
        return `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encoded}`;
    }, [url]);

    const handleCloseModal = () => closeModal(() => hideModal())

    const printRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: "Zdrowio",
        pageStyle: printPageStyle,
        onBeforePrint: async () => setPrintPrepare(true),
        onAfterPrint: async () => setPrintPrepare(false),
        // print: async () => setPrintPrepare(false),
    });

    if (!isOpenModal() || !title || !url) return null;

    return (
        <AppModal
            id={ModalIds.ARTICLE_QR_INFO}
            key={ModalIds.ARTICLE_QR_INFO}
            zIndex={50}
            forceFixed={true}
            classModal="min-h-full min-w-full max-w-[calc(100%-100px)] max-h-svh flex justify-center overflow-hidden"
        >
            <animated.div
                style={springProps}
                className="flex flex-col bg-surface-level-1 rounded-2xl w-full max-h-full overflow-y-auto"
            >
                <div ref={printRef} className="print-only">
                    <div className="sm:px-8 px-6 sm:pt-8 sm:pb-6 pt-4 pb-3">
                        <h2 className="text-center text-2xl font-semibold leading-[30px] break-words">
                            {stripHtmlToText(convertLexicalJsonToHtml(title))}
                        </h2>
                    </div>

                    <div className="sm:px-8 px-6 sm:pt-8 sm:pb-6 pt-4 pb-3 flex justify-center">
                        {qrSrc && <QrCode src={qrSrc} size={256}/>}
                    </div>

                    <div className="space-y-6 sm:px-8 px-6 sm:pt-8 sm:pb-6 pt-4 pb-3">
                        <p className="font-semibold text-lg leading-[22px]">
                            Zeskanuj kod, aby uzyskać szczegółowe informacje o schorzeniu.
                        </p>
                        <ul className="flex flex-col gap-2">
                            {defaultQrCodeBullets.map((item, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-on-surface ">
                                <span className="h-5 w-5 min-w-5 rounded-full bg-primary text-on-primary">
                                    <CheckmarkCircleIcon className="w-full h-full text-black bg-white"/>
                                </span>
                                    <span className="text-base font-normal leading-5">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="flex flex-wrap md:flex-nowrap items-center justify-end gap-3 px-8 min-h-[72px]">
                    <AppButton
                        disabled={printPrepare}
                        onClick={handlePrint}
                        classNameContentWrapper="!flex !flex-row-reverse !gap-1 [&>div]:w-full"
                        className="w-full md:w-fit !p-0 hover:bg-on-surface/5 !rounded-full border-[2px] !border-on-surface font-semibold"
                    >
                        <div className="py-2 pl-4 pr-6 justify-center flex items-center text-on-surface">
                            <span className="mr-2">Drukuj</span>
                            <PrintIcon/>
                            <div className="relative left-2">
                                {printPrepare && <SpinnerLoader color="#333"/>}
                            </div>
                        </div>
                    </AppButton>
                    <AppButton
                        label="Zamknij"
                        onClick={() => handleCloseModal()}
                        className="w-full md:w-fit bg-primary hover:bg-primary/80 max-h-[40px] h-[40px] !px-6 !pt-2 text-on-primary font-semibold text-base !rounded-full"
                    />
                </div>
            </animated.div>
        </AppModal>
    );
}

export default ArticleQrInfoModal;


